import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, Smartphone, DollarSign, Check } from "lucide-react";
import api, { mediaUrl, apiErr } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { toast } from "sonner";

export default function Checkout() {
  const { items, subtotal, count, clear } = useCart();
  const { user } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", email: "", phone: "", street: "", unit: "", city: "", state: "", zip: "", country: "", gift_note: "", newsletter: true });
  const [promo, setPromo] = useState("");
  const [applied, setApplied] = useState(null);
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("stripe");
  const [manualResult, setManualResult] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const pay = settings?.payments || {};
  const methods = [];
  if (pay.stripe_enabled !== false) methods.push({ id: "stripe", label: "Credit / Debit Card", sub: "Apple Pay & Google Pay via Stripe", icon: CreditCard });
  if (pay.manual_enabled && pay.cashtag) methods.push({ id: "cashapp", label: "Cash App", sub: pay.cashtag, icon: DollarSign });
  if (pay.manual_enabled && pay.venmo) methods.push({ id: "venmo", label: "Venmo", sub: pay.venmo, icon: Smartphone });
  if ((pay.paypal_enabled || pay.manual_enabled) && pay.paypal_handle) methods.push({ id: "paypal", label: "PayPal", sub: pay.paypal_handle, icon: Smartphone });

  useEffect(() => { if (user) setF((p) => ({ ...p, name: user.name || "", email: user.email || "", phone: user.phone || "", ...(user.address || {}) })); }, [user]);
  useEffect(() => { if (methods.length && !methods.some((m) => m.id === method)) setMethod(methods[0].id); }, [settings]); // eslint-disable-line

  const FREE = 200;
  const discount = applied?.discount || 0;
  const shipping = subtotal >= FREE ? 0 : 15;
  const total = Math.max(0, subtotal - discount + shipping);

  const applyPromo = async () => {
    if (!promo) return;
    try { const { data } = await api.post("/promo/validate", { code: promo, subtotal }); setApplied(data); toast.success(`Promo ${data.code} applied.`); }
    catch (e) { setApplied(null); toast.error(apiErr(e)); }
  };

  const buildPayload = () => ({
    items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
    email: f.email, name: f.name, phone: f.phone,
    shipping: { street: f.street, unit: f.unit, city: f.city, state: f.state, zip: f.zip, country: f.country },
    promo_code: applied?.code || "", affiliate_ref: localStorage.getItem("sv_ref") || "",
    gift_note: f.gift_note, newsletter: f.newsletter, origin_url: window.location.origin,
  });

  const payNow = async () => {
    if (items.length === 0) { toast.error("Your bag is empty."); return; }
    if (!f.name || !f.email) { toast.error("Please add your name and email."); return; }
    setLoading(true);
    try {
      if (method === "stripe") {
        const { data } = await api.post("/checkout", buildPayload());
        window.location.href = data.checkout_url;
      } else {
        const { data } = await api.post("/checkout/manual", { ...buildPayload(), method });
        setManualResult(data);
        clear();
      }
    } catch (e) { toast.error(apiErr(e)); setLoading(false); }
  };

  if (manualResult) return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-20 text-center" data-testid="manual-success">
      <div className="max-w-md">
        <div className="w-16 h-16 rounded-full bg-ink text-linen flex items-center justify-center mx-auto mb-6"><Check size={28} strokeWidth={1.5} /></div>
        <h1 className="font-heading text-4xl tracking-tight mb-2">Order Reserved</h1>
        <p className="font-body text-sm">Order <b>{manualResult.order_number}</b> · Total <b>${manualResult.total?.toFixed(2)}</b></p>
        <div className="bg-cream border hairline p-6 mt-6 text-left">
          <p className="eyebrow mb-2">Send Payment To</p>
          <p className="font-heading text-3xl mb-3">{manualResult.handle || method}</p>
          <p className="font-body text-sm text-ink/70">{manualResult.instructions}</p>
          <p className="font-body text-sm mt-3">Please include order <b>{manualResult.order_number}</b> in your payment note.</p>
        </div>
        <button onClick={() => navigate("/")} className="btn-primary mt-8">Back to Home</button>
      </div>
    </div>
  );

  if (items.length === 0) return (
    <div className="py-32 text-center"><p className="font-heading text-3xl text-ink/50 mb-6">Your bag is empty.</p><button onClick={() => navigate("/shop")} className="btn-primary">Shop the Voyage</button></div>
  );

  return (
    <div className="px-6 md:px-12 py-14 mx-auto max-w-[1100px] grid lg:grid-cols-[1.3fr_1fr] gap-14" data-testid="checkout-page">
      <div>
        <h1 className="font-heading text-4xl tracking-tight mb-8">Checkout</h1>
        <p className="eyebrow mb-4">Contact</p>
        <div className="grid grid-cols-2 gap-5 mb-10">
          <input className="input-luxe col-span-2" placeholder="Full Name" value={f.name} onChange={set("name")} data-testid="checkout-name" />
          <input className="input-luxe" type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="checkout-email" />
          <input className="input-luxe" placeholder="Phone" value={f.phone} onChange={set("phone")} data-testid="checkout-phone" />
        </div>
        <p className="eyebrow mb-4">Shipping Address</p>
        <div className="grid grid-cols-2 gap-5 mb-10">
          <input className="input-luxe col-span-2" placeholder="Street Address" value={f.street} onChange={set("street")} data-testid="checkout-street" />
          <input className="input-luxe" placeholder="Unit / Suite" value={f.unit} onChange={set("unit")} />
          <input className="input-luxe" placeholder="City" value={f.city} onChange={set("city")} data-testid="checkout-city" />
          <input className="input-luxe" placeholder="State / Province" value={f.state} onChange={set("state")} />
          <input className="input-luxe" placeholder="ZIP / Postal" value={f.zip} onChange={set("zip")} />
          <input className="input-luxe col-span-2" placeholder="Country" value={f.country} onChange={set("country")} data-testid="checkout-country" />
        </div>

        <p className="eyebrow mb-4">Payment Method</p>
        <div className="space-y-3 mb-10">
          {methods.map((m) => (
            <button key={m.id} onClick={() => setMethod(m.id)} data-testid={`pay-method-${m.id}`}
                    className={`w-full flex items-center gap-4 border p-4 text-left transition-colors ${method === m.id ? "border-ink bg-cream" : "border-ink/20 hover:border-ink/50"}`}>
              <m.icon size={20} strokeWidth={1.5} />
              <div className="flex-1"><p className="font-body text-sm font-medium">{m.label}</p><p className="font-body text-xs text-ink/50">{m.sub}</p></div>
              <span className={`w-4 h-4 rounded-full border ${method === m.id ? "bg-ink border-ink" : "border-ink/40"}`} />
            </button>
          ))}
        </div>

        <p className="eyebrow mb-4">Gift Note (optional)</p>
        <textarea className="input-luxe" rows={2} placeholder="Add a personal message…" value={f.gift_note} onChange={set("gift_note")} />
        <label className="flex items-center gap-3 font-body text-sm text-ink/70 mt-6"><input type="checkbox" checked={f.newsletter} onChange={set("newsletter")} className="accent-ink w-4 h-4" data-testid="checkout-newsletter" />Keep me posted on new drops</label>
      </div>

      <div className="lg:border-l hairline lg:pl-10">
        <h2 className="font-heading text-2xl mb-6">Order Summary <span className="text-ink/40 text-lg">({count})</span></h2>
        <div className="space-y-4 mb-6">
          {items.map((i) => (
            <div key={`${i.product_id}-${i.size}`} className="flex gap-3 items-center">
              <div className="w-14 h-18 bg-cream overflow-hidden"><img src={mediaUrl(i.image)} alt={i.title} className="w-full h-full object-cover" /></div>
              <div className="flex-1"><p className="font-heading text-base leading-tight">{i.title}</p><p className="font-body text-xs text-ink/50">{i.size} · Qty {i.quantity}</p></div>
              <span className="font-body text-sm">${(i.price * i.quantity).toFixed(2)}</span>
            </div>
          ))}
        </div>
        <div className="flex gap-2 mb-6">
          <input className="input-luxe flex-1" placeholder="Promo code" value={promo} onChange={(e) => setPromo(e.target.value)} data-testid="checkout-promo-input" />
          <button onClick={applyPromo} className="btn-outline text-[0.65rem]" data-testid="checkout-promo-apply">Apply</button>
        </div>
        <div className="space-y-2 font-body text-sm border-t hairline pt-4">
          <Row l="Subtotal" v={`$${subtotal.toFixed(2)}`} />
          {discount > 0 && <Row l={`Discount (${applied.code})`} v={`− $${discount.toFixed(2)}`} />}
          <Row l="Shipping" v={shipping === 0 ? "Complimentary" : `$${shipping.toFixed(2)}`} />
          <div className="flex justify-between pt-3 border-t hairline"><span className="font-body uppercase tracking-wide2">Total</span><span className="font-heading text-2xl" data-testid="checkout-total">${total.toFixed(2)}</span></div>
        </div>
        <button onClick={payNow} disabled={loading} className="btn-primary w-full mt-6" data-testid="checkout-pay-btn">
          {loading ? "Processing…" : method === "stripe" ? "Pay Securely" : "Place Order"}
        </button>
        <p className="font-body text-[0.65rem] text-ink/50 text-center mt-3">{method === "stripe" ? "Secured by Stripe · Test card 4242 4242 4242 4242" : "You'll receive payment instructions to complete your order."}</p>
      </div>
    </div>
  );
}
const Row = ({ l, v }) => <div className="flex justify-between text-ink/70"><span>{l}</span><span>{v}</span></div>;
